import {createSelector} from 'reselect'
import omitBy from 'lodash/omitBy.js'
import get from 'lodash/get.js'
import chunk from 'lodash/chunk.js'

import {
  OT_V3_ORDER,
  OT_RETURN_ORDER,
} from '../../common/constants/OrderTypes.js'
import {
  AMAZON_SFP,
  AMAZON_SHIPPER,
  AUSTRALIA_POST,
  CANADA_POST,
  PITNEY_CBDS,
  DHL,
  DHL_ECOMMERCE,
  ENDICIA,
  FEDEX,
  NEWGISTICS,
  PITNEY,
  PITNEY_MERCHANT,
  PITNEY_PRESORT,
  SENDLE,
  UPS,
  USPS_SHIP,
  FEDEX_360,
  SHIPPO,
} from '../../common/constants/ShipperNames.js'
import {
  FEDEX_SHIPPER_DEFAULT_HUB,
  LABEL_TYPE_RETURN,
} from '../../common/constants/LabelConfig.js'
import {combineToOz} from '../../common/weight.js'
import {isEmptyValue} from '../../common/utils.js'
import {parseNonZeroPositiveNumber} from '../../common/parseNumbers.js'
import getShipDate from '../../common/getShipDate.js'
import {
  companySelector,
  usePresetMultiShipperSelector,
} from '../../data/company.js'
import {orderLinkSelector, updateOrders} from '../orders.js'
import {
  labelConfigSelector,
  labelShipperSelector,
  labelShipperTypeSelector,
  labelTypeSelector,
  getTotalPackageWeight,
  labelShipFromAddressSelector,
  labelShipToAddressSelector,
  getMaxParcelCount,
  orderTypeSelector,
  orderNumberFromLabelConfigSelector,
  selectedRateSelector,
  assertAmazonPrimeSanity,
  referenceIDFromLabelConfigSelector,
  updateLabelInfo,
  saveLabelInfo,
  orderNumbersFromLabelConfigsSelector,
  getDimensionsParams,
  getInsuranceParams,
  getCanHaveHarmonizationCode,
  getCanHaveSKU,
  getCanHaveHarmonizationCodeCountry,
  SENDER_TAX_INFO,
  RECEIVER_TAX_INFO,
  taxIDNumberTemplate,
  taxIDTypeTemplate,
  taxIssuerCountryTemplate,
  TAX_INFO_NEEDS_COUNTRY_SHIPPERS,
  getUnifiedOptions,
  getCanHaveEUPIDs,
  LABEL_PROPERTY_SEND_EU_PRODUCT_IDENTIFIERS,
} from '../labelInfos/index.js'
import {getReturnLabelParams} from './rateRequest.js'
import {getState} from '../../store.js'
import apiverson from '../../common/apiverson.js'
import {updateReturnOrders} from '../../redux/actions/data/returnOrders.js'
import {currentPageSelector} from '../../redux/selectors/ui/index.js'
import {
  ORDER_DETAIL_PAGE,
  ORDER_LIST_PAGE,
  PACK_SHIP_PAGE,
} from '../../common/constants/Pages.js'
import {
  addPendingLabels,
  removePendingLabels,
} from '../../ordoro/OrderListPage/ActionPanel/orderActionPanelFunctions.js'
import {refreshOrderList} from '../../ordoro/OrderListPage/orderListActions.js'
import {loadOrderDetailOrder} from '../../ordoro/OrderDetailPage/orderDetailActions.js'
import {logError} from '../../common/error.js'
import {updateOrderCounts} from '../orderCounts.js'
import {showProcessLabelModal} from '../../ordoro/OrderListPage/Modals/ProcessLabelModal.js'

export function getCustomsParams(config, shipperType) {
  if (
    !config.attach_customs_info ||
    [AMAZON_SFP, AMAZON_SHIPPER, PITNEY_PRESORT].includes(shipperType)
  ) {
    return {}
  }

  const canHaveHarmonizationCode = getCanHaveHarmonizationCode(shipperType)
  const canHaveHarmonizationCodeCountry =
    getCanHaveHarmonizationCodeCountry(shipperType)
  const canHaveSKU = getCanHaveSKU(shipperType)
  const includeEUPIDs =
    getCanHaveEUPIDs(shipperType) &&
    config[LABEL_PROPERTY_SEND_EU_PRODUCT_IDENTIFIERS]
  const unitOfMeasureOptions = getUnifiedOptions(
    [shipperType],
    'unit_of_measure',
  )

  const lines = config.customs_info.map((l) => {
    const line = {
      description: l.description,
      quantity: Number(l.quantity),
      value: Number(l.value),
      weight: combineToOz(l.weightLB, l.weightOZ) / Number(l.quantity),
      country: l.country,
    }

    if (canHaveSKU && l.sku) {
      line.sku = l.sku
    }

    if (canHaveHarmonizationCode && l.harmonizationCode) {
      line.harmonization_code = l.harmonizationCode
    }

    if (canHaveHarmonizationCodeCountry && l.harmonizationCodeCountry) {
      line.harmonization_code_country = l.harmonizationCodeCountry
    }

    if (includeEUPIDs) {
      if (l.m_pid) {
        line.m_pid = l.m_pid
      }

      if (l.ns_pid) {
        line.ns_pid = l.ns_pid
      }

      if (l.s_pid) {
        line.s_pid = l.s_pid
      }
    }

    if (unitOfMeasureOptions.length && l.unit_of_measure) {
      const unitOfMeasureOption = unitOfMeasureOptions.find(
        (option) => option.value === l.unit_of_measure,
      )

      if (unitOfMeasureOption) {
        line.unit_of_measure = unitOfMeasureOption.value
      }
    }

    return line
  })

  const customsParams = {customs_info: lines}

  if (shipperType === FEDEX) {
    customsParams.etd_service = config.etd_service
  } else if (shipperType === FEDEX_360) {
    customsParams.etd_service_default_false = config.etd_service_default_false
  } else if (shipperType === UPS) {
    customsParams.purchase_order_number =
      config.purchase_order_number || undefined
    customsParams.sender_type = config.sender_type
    customsParams.receiver_type = config.receiver_type
  }

  return customsParams
}

export function getHazmatParams(parcel, shipperType) {
  if (shipperType !== UPS) {
    return {}
  }

  const boxShape = parcel[`${shipperType}__box_shape`]

  // Don't send hazmat for Letter (we already hide UI)
  if (boxShape === '01') {
    return {}
  }

  if (!parcel.hazmat_items) {
    return {}
  }

  return {
    hazmat_items: parcel.hazmat_items.map((hazmatItem) => ({
      commodity_regulated_level_code:
        hazmatItem[`${shipperType}__commodity_regulated_level_code`],
      regulation_set: hazmatItem[`${shipperType}__regulation_set`],
      transportation_mode: hazmatItem[`${shipperType}__transportation_mode`],
      technical_name: hazmatItem.technical_name,
      additional_description: hazmatItem.additional_description,
      class_division_number: hazmatItem.class_division_number,
      hazard_label_required: hazmatItem.hazard_label_required,
      id_number: hazmatItem.id_number,
      packaging_instruction_code: hazmatItem.packaging_instruction_code,
      packaging_group_type: hazmatItem[`${shipperType}__packaging_group_type`],
      packaging_type: hazmatItem.packaging_type,
      packaging_type_quantity: hazmatItem.packaging_type_quantity,
      proper_shipping_name: hazmatItem.proper_shipping_name,
      quantity: hazmatItem.quantity,
      reportable_quantity: hazmatItem.reportable_quantity,
      sub_risk_class: hazmatItem.sub_risk_class,
      uom: hazmatItem.uom,
      emergency_contact: hazmatItem.emergency_contact,
      emergency_phone: hazmatItem.emergency_phone,
    })),
  }
}

function selectedShippingMethod(config, shipperType, rate) {
  return rate?.service_type || config[`${shipperType}__shipping_method`]
}

export function getSharedParams(
  config,
  shipper,
  labelType,
  shipFromAddress,
  shipToAddress,
  rate,
) {
  const shipperType = shipper.vendor

  return {
    shipper_id: rate.shipper_id || config.shipper_id,
    delivery_confirmation: config[`${shipperType}__delivery_confirmation`],
    shipping_method: selectedShippingMethod(config, shipperType, rate),
    notify_bill_to: config.notify_bill_to,
    notify_ship_to: config.notify_ship_to,
    reference_number: config.reference_number,
    packing_list_id: config.packing_list_id,
    ship_from: shipFromAddress,
    ship_to: shipToAddress,
    ...getInsuranceParams(config, shipperType),
    ...getReturnLabelParams(labelType),
    ...getCustomsParams(config, shipperType),
    reference_id: config.reference_id,
  }
}

export function getPackagesParam({
  config,
  shipperType,
  otherParams = [{}],
  rate,
}) {
  const usePresetMultiShipper = usePresetMultiShipperSelector(getState())
  const maxParcelCount = getMaxParcelCount(shipperType)
  const packages = get(config, 'packages', [{}]).slice(0, maxParcelCount)
  const rateBoxShape = usePresetMultiShipper ? rate?.box_type : null
  const individualBoxShapes =
    shipperType === UPS &&
    selectedShippingMethod(config, shipperType, rate) === '01'

  return {
    packages: packages.map((parcel, index) => {
      const boxShape =
        individualBoxShapes || !rateBoxShape
          ? parcel[`${shipperType}__box_shape`]
          : rateBoxShape

      return {
        ...omitBy(
          {
            box_shape: boxShape,
            weight: getTotalPackageWeight(parcel.weight, config),
            ...getDimensionsParams(parcel, boxShape, shipperType),
            ...getHazmatParams(parcel, shipperType),
          },
          isEmptyValue,
        ),
        ...get(otherParams, index, {}),
      }
    }),
  }
}

export function getBoxShapeParam(config, shipperType) {
  if ([NEWGISTICS, SENDLE].includes(shipperType)) {
    return null
  }

  return config.boxShape
}

export function getTaxInfo(config, shipperType, property) {
  const idLabelProperty = taxIDNumberTemplate(property)
  const typeLabelProperty = taxIDTypeTemplate(shipperType, property)
  const countryLabelProperty = taxIssuerCountryTemplate(property)

  if (config[idLabelProperty]) {
    return {
      [property]: {
        tax_id_number: config[idLabelProperty],
        tax_id_type: config[typeLabelProperty],
        ...(TAX_INFO_NEEDS_COUNTRY_SHIPPERS.includes(shipperType)
          ? {
              tax_issuer_country: config[countryLabelProperty],
            }
          : null),
      },
    }
  }
}

export function getUpsParams(config, rate) {
  return {
    certificate_origin: config.certificate_origin,
    saturday_delivery: rate.saturday_delivery,
    direct_delivery: config.direct_delivery,
    alternate_return_to_address: config.use_alternate_return_to_address
      ? config.alternate_return_to_address
      : null,
    recipient_address_is_residential: config.recipient_address_is_residential,
    shipper_release: config.shipper_release,
    delivery_confirmation: config.can_have_delivery_confirmation
      ? config.ups__delivery_confirmation
      : null,
    package_bill_type: config.ups__package_bill_type,
    reason_for_export: config.ups__reason_for_export,
    ...getUpsPaymentParams(config),
    ...getUpsDutiesPaymentParams(config),
    ...getUpsMailInnovationsParams(config),
    ...getTaxInfo(config, UPS, SENDER_TAX_INFO),
    ...getTaxInfo(config, UPS, RECEIVER_TAX_INFO),
    ...getPackagesParam({
      config,
      shipperType: UPS,
      otherParams: get(config, 'packages', []).map((parcel) => {
        const dryIceWeight = parseNonZeroPositiveNumber(parcel.dry_ice_weight)

        return omitBy(
          {
            additional_handling: parcel.additional_handling || null,
            declared_value: parseNonZeroPositiveNumber(
              parcel.ups__declared_value,
            ),
            dry_ice_weight: dryIceWeight,
            dry_ice_regulation_set: dryIceWeight
              ? parcel.dry_ice_regulation_set
              : null,
          },
          isEmptyValue,
        )
      }),
      rate,
    }),
  }
}

export function getUpsPaymentParams(config) {
  if (config.ups__payment_type === 'BillShipper') {
    return {}
  }

  const params = {
    payment_type: config.ups__payment_type,
    payment_account: config.ups__payment_account,
  }

  if (config.ups__payment_type === 'BillThirdParty') {
    Object.assign(params, {
      payment_zip: config.ups__payment_zip,
      payment_country: config.ups__payment_country,
    })
  }

  return params
}

export function getUpsDutiesPaymentParams(config) {
  if (config.ups__duties_payment_type === 'BillShipper') {
    return {}
  }

  const params = {
    duties_payment_type: config.ups__duties_payment_type,
    duties_payment_account: config.ups__duties_payment_account,
  }

  if (config.ups__duties_payment_type === 'BillThirdParty') {
    Object.assign(params, {
      duties_payment_zip: config.ups__duties_payment_zip,
      duties_payment_country: config.ups__duties_payment_country,
    })
  }

  return params
}

export function getUpsMailInnovationsParams(config) {
  if (!config.is_mail_innovations) {
    return {}
  }

  return {
    usps_endorsement: config.ups__usps_endorsement,
    cost_center: config.cost_center,
  }
}

export function getFedExParams(config, rate, shipper) {
  return {
    ship_date: getShipDate(config.ship_date),
    delivery_confirmation: config[`${FEDEX}__delivery_confirmation`],
    delivery_instructions: config.delivery_instructions,
    alternate_return_to_address: config.use_alternate_return_to_address
      ? config.alternate_return_to_address
      : null,
    recipient_address_is_residential: config.recipient_address_is_residential,
    certificate_of_origin: config.certificate_origin,
    saturday_delivery: rate.saturday_delivery,
    hold_at_location_id: config[`${FEDEX}__hold_at_location_id`],
    smart_post_hub:
      config[`${FEDEX}__smart_post_hub`] === FEDEX_SHIPPER_DEFAULT_HUB
        ? get(shipper, 'vendor_config.smartpost_hub') || ''
        : config[`${FEDEX}__smart_post_hub`],
    smart_post_indicia: config[`${FEDEX}__smart_post_indicia`],
    ancillary_endorsement: config[`${FEDEX}__ancillary_endorsement`],
    alcohol_shipment_license: config[`${FEDEX}__alcohol_shipment_license`],
    dangerous_goods_option: config[`${FEDEX}__dangerous_goods_option`],
    reason_for_export: config[`${FEDEX}__reason_for_export`],
    terms_of_sale: config[`${FEDEX}__terms_of_sale`],
    importer_of_record:
      config.attach_customs_info &&
      config.include_importer_of_record &&
      config.importer_of_record
        ? config.importer_of_record
        : null,
    pharmacy_delivery: config.pharmacy_delivery,
    priority_alert: get(shipper, 'vendor_config.has_priority_alert')
      ? config[`${FEDEX}__priority_alert`]
      : null,
    label_type: config[`${FEDEX}__label_type`],
    etd_preshipment_docs: (config[`${FEDEX}__etd_preshipment_docs`] || [])
      .length
      ? config[`${FEDEX}__etd_preshipment_docs`]
      : null,
    one_rate: rate.one_rate,
    b13a_filing_option:
      (config.attach_customs_info && config[`${FEDEX}__b13a_filing_option`]) ||
      null,
    export_compliance_statement:
      (config.attach_customs_info && config.export_compliance_statement) ||
      null,
    ...getFedExPaymentParams(config, FEDEX),
    ...getFedExDutiesParams(config, FEDEX),
    ...getTaxInfo(config, FEDEX, SENDER_TAX_INFO),
    ...getTaxInfo(config, FEDEX, RECEIVER_TAX_INFO),
    ...getPackagesParam({
      config,
      shipperType: FEDEX,
      otherParams: get(config, 'packages', []).map((parcel) =>
        omitBy(
          {
            declared_value: parseNonZeroPositiveNumber(
              parcel[`${FEDEX}__declared_value`],
            ),
            dry_ice_weight: parseNonZeroPositiveNumber(parcel.dry_ice_weight),
            invoice: config.invoice_number,
            purchase_order: config.purchase_order_number,
            department: config.department_number,
            sub_package_type: parcel[`${FEDEX}__sub_package_type`],
          },
          isEmptyValue,
        ),
      ),
      rate,
    }),
  }
}

export function getFedEx360Params(config, rate) {
  return {
    alternate_return_to_address: config.use_alternate_return_to_address
      ? config.alternate_return_to_address
      : null,
    delivery_confirmation: config[`${FEDEX_360}__delivery_confirmation`],
    reason_for_export: config[`${FEDEX_360}__reason_for_export`],
    reason_for_export_explanation: config.reason_for_export_explanation,
    recipient_address_is_residential: config.recipient_address_is_residential,
    saturday_delivery: config.saturday_delivery,
    ship_date: getShipDate(config.ship_date),
    ...(config[`${FEDEX_360}__battery_material`]
      ? {
          batteries: {
            material: config[`${FEDEX_360}__battery_material`],
            packaging: config[`${FEDEX_360}__battery_packaging`],
          },
        }
      : null),
    ...getFedExPaymentParams(config, FEDEX_360),
    ...getFedExDutiesParams(config, FEDEX_360),
    ...getPackagesParam({
      config,
      shipperType: FEDEX_360,
      otherParams: get(config, 'packages', []).map((parcel) =>
        omitBy(
          {
            declared_value: parseNonZeroPositiveNumber(
              parcel[`${FEDEX_360}__declared_value`],
            ),
            dry_ice_weight: parseNonZeroPositiveNumber(parcel.dry_ice_weight),
            invoice: config.invoice_number,
            purchase_order: config.purchase_order_number,
            department: config.department_number,
          },
          isEmptyValue,
        ),
      ),
      rate,
    }),
  }
}

export function getFedExPaymentParams(config, shipperType) {
  const params = {
    payment_type: config[`${shipperType}__payment_type`],
  }

  if (config[`${shipperType}__payment_type`] !== 'SENDER') {
    Object.assign(params, {
      payment_account: config[`${shipperType}__payment_account`],
    })
  }

  return params
}

export function getFedExDutiesParams(config, shipperType) {
  const params = {
    duties_payment_type: config[`${shipperType}__duties_payment_type`],
  }

  if (config.duties_payment_type !== 'SENDER') {
    Object.assign(params, {
      duties_payment_account: config[`${shipperType}__duties_payment_account`],
    })
  }

  return params
}

export function getEndiciaParams(config, rate) {
  return {
    ship_date: getShipDate(config.ship_date),
    contents_type: config.endicia__contents_type,
    hold_for_pickup: config.hold_for_pickup,
    nondelivery_option: config.endicia__nondelivery_option,
    mailing_zip_code: config.mailing_zip_code,
    ...getPackagesParam({
      config,
      shipperType: ENDICIA,
      rate,
    }),
  }
}

export function getPitneyParams(config, rate, company) {
  return {
    alternate_return_to_address: config.use_alternate_return_to_address
      ? config.alternate_return_to_address
      : null,
    ship_date: getShipDate(config.ship_date),
    nondelivery_option: config.pitney__nondelivery_option,
    reason_for_export: config.pitney__reason_for_export,
    reason_for_export_explanation: config.reason_for_export_explanation,
    pitney_rate_type: company.pitney_rate_type,
    rate_cost: rate && rate.cost,
    mailing_zip_code: config.mailing_zip_code,
    hazmat_type: config[`${PITNEY}__hazmat_type`],
    hazmat_electronics: config.hazmat_electronics,
    parcel_characteristics: config[`${PITNEY}__parcel_characteristics`],
    ...getTaxInfo(config, PITNEY, SENDER_TAX_INFO),
    ...getPackagesParam({
      config,
      shipperType: PITNEY,
      rate,
    }),
  }
}

export function getPitneyMerchantParams(config, rate, company) {
  return {
    alternate_return_to_address: config.use_alternate_return_to_address
      ? config.alternate_return_to_address
      : null,
    ship_date: getShipDate(config.ship_date),
    nondelivery_option: config.pitney_merchant__nondelivery_option,
    reason_for_export: config.pitney_merchant__reason_for_export,
    reason_for_export_explanation: config.reason_for_export_explanation,
    pitney_rate_type: company.pitney_rate_type,
    rate_cost: rate && rate.cost,
    mailing_zip_code: config.mailing_zip_code,
    hazmat_type: config[`${PITNEY_MERCHANT}__hazmat_type`],
    hazmat_electronics: config.hazmat_electronics,
    parcel_characteristics:
      config[`${PITNEY_MERCHANT}__parcel_characteristics`],
    ...getTaxInfo(config, PITNEY_MERCHANT, SENDER_TAX_INFO),
    ...getPackagesParam({
      config,
      shipperType: PITNEY_MERCHANT,
      rate,
    }),
  }
}

export function getPitneyPresortParams(config, rate) {
  return {
    nondelivery_option: config.pitney__nondelivery_option,
    reason_for_export: config.pitney__reason_for_export,
    reason_for_export_explanation: config.reason_for_export_explanation,
    rate_cost: rate && rate.cost,
    mailing_zip_code: config.mailing_zip_code,
    ...getPackagesParam({
      config,
      shipperType: PITNEY_PRESORT,
      rate,
    }),
  }
}

export function getCanadaPostParams(config, rate) {
  return {
    nondelivery_option: config.canada_post__nondelivery_option,
    reason_for_export: config.canada_post__reason_for_export,
    service_option: config.canada_post__service_option,
    invoice_number: config.invoice_number,
    ...getPackagesParam({
      config,
      shipperType: CANADA_POST,
      rate,
    }),
  }
}

export function getPitneyCBDSPostParams(config, rate) {
  return {
    ...getPackagesParam({
      config,
      shipperType: PITNEY_CBDS,
      rate,
    }),
  }
}

export function getDHLParams(config, rate) {
  return {
    nonstandard_day: config.dhl__nonstandard_day,
    nonstandard_contents: config.dhl__nonstandard_contents,
    signature_service: config.dhl__signature_service,
    is_dutiable: !!config.is_dutiable,
    federal_tax_id: config.federal_tax_id,
    state_tax_id: config.state_tax_id,
    ...(() => {
      const params = {
        shipping_payment_type: config.dhl__payment_type,
      }

      if (config.dhl__payment_type !== 'S') {
        Object.assign(params, {
          shipping_payment_account: config.dhl__payment_account,
        })
      }

      return params
    })(),
    ...(() => {
      const params = {
        duty_payment_type: config.dhl__duty_payment_type,
      }

      if (config.dhl__duty_payment_type !== 'S') {
        Object.assign(params, {
          duty_payment_account: config.dhl__duties_payment_account,
        })
      }

      return params
    })(),
    reason_for_export: config.dhl__reason_for_export,
    reason_for_export_explanation: config.reason_for_export_explanation,
    foreign_trade_regulation: config.dhl__foreign_trade_regulation || null,
    internal_transaction_number: config.dhl__foreign_trade_regulation
      ? null
      : config.internal_transaction_number,
    ...getTaxInfo(config, DHL, SENDER_TAX_INFO),
    ...getTaxInfo(config, DHL, RECEIVER_TAX_INFO),
    ...getPackagesParam({
      config,
      shipperType: DHL,
      otherParams: get(config, 'packages', []).map((parcel) =>
        omitBy(
          {
            declared_value:
              parcel.dhl__declared_value && Number(parcel.dhl__declared_value),
          },
          isEmptyValue,
        ),
      ),
      rate,
    }),
  }
}

export function getDHLEcommerceParams(config, rate, labelType) {
  return {
    ship_date: getShipDate(config.ship_date),
    dangerous_goods: config[`${DHL_ECOMMERCE}__dangerous_goods`],
    delivery_confirmation: config[`${DHL_ECOMMERCE}__delivery_confirmation`],
    service_endorsement: config[`${DHL_ECOMMERCE}__service_endorsement`],
    return_reason:
      labelType === LABEL_TYPE_RETURN ? config.return_reason : null,
    ...getTaxInfo(config, DHL_ECOMMERCE, RECEIVER_TAX_INFO),
    ...getPackagesParam({
      config,
      shipperType: DHL_ECOMMERCE,
      otherParams: get(config, 'packages', []).map((parcel) =>
        omitBy(
          {
            description: parcel.description || '',
            declared_value: parseNonZeroPositiveNumber(
              parcel[`${DHL_ECOMMERCE}__declared_value`],
            ),
          },
          isEmptyValue,
        ),
      ),
      rate,
    }),
  }
}

export function getAmazonParams(config, rate) {
  return {
    ship_date: getShipDate(config.ship_date),
    carrier_pickup: config.carrier_pickup,
    is_hazmat: config.is_hazmat,
    reference_number: undefined,
    ship_to: undefined,
    ...getPackagesParam({
      config,
      shipperType: AMAZON_SFP,
      rate,
    }),
  }
}

export function getAmazonShipperParams(config, rate) {
  return {
    recipient_address_is_residential: config.recipient_address_is_residential,
    ...(config.pickup_start_date && config.pickup_end_date
      ? {
          expected_service_offering: {
            start_date: config.pickup_start_date,
            end_date: config.pickup_end_date,
          },
        }
      : undefined),
    service_token: get(rate, 'service_token'),
    shipment_id: get(rate, 'shipment_id'),
    is_hazmat: config.is_hazmat,
    ...getPackagesParam({
      config,
      shipperType: AMAZON_SHIPPER,
      otherParams: get(config, 'packages', []).map((parcel) =>
        omitBy(
          {
            declared_value: parseNonZeroPositiveNumber(
              parcel.amazon_shipper__declared_value,
            ),
          },
          isEmptyValue,
        ),
      ),
      rate,
    }),
  }
}

export function getNewgisticsParams(config, rate) {
  return {
    nondelivery_option: config.newgistics__nondelivery_option,
    enable_notifications: config.enable_notifications,
    dangerous_goods_option: config.newgistics__dangerous_goods,
    reason_for_export: config.newgistics__reason_for_export,
    reason_for_export_explanation: config.reason_for_export_explanation,
    ...getPackagesParam({
      config,
      shipperType: NEWGISTICS,
      rate,
    }),
  }
}

export function getSendleParams(config, rate) {
  const params = {
    pickup_date: config.pickup_date || undefined,
    reason_for_export: config[`${SENDLE}__reason_for_export`],
    ...getPackagesParam({
      config,
      shipperType: SENDLE,
      otherParams: get(config, 'packages', []).map((parcel) => ({
        description: parcel.description || '',
      })),
      rate,
    }),
  }

  return params
}

export function getShippoParams(config, rate) {
  return {
    ship_date: getShipDate(config.ship_date),
    delivery_confirmation: config[`${SHIPPO}__delivery_confirmation`],
    delivery_instructions: config.delivery_instructions,
    alternate_return_to_address: config.use_alternate_return_to_address
      ? config.alternate_return_to_address
      : null,
    saturday_delivery: config.saturday_delivery,
    ancillary_endorsement: config[`${SHIPPO}__ancillary_endorsement`],
    alcohol_shipment_license: config[`${SHIPPO}__alcohol_shipment_license`],
    dangerous_goods_option: config[`${SHIPPO}__dangerous_goods`],
    reason_for_export: config[`${SHIPPO}__reason_for_export`],
    ignore_bad_address: config.ignore_bad_address,
    shipment_id: get(rate, 'shipment_id'),
    rate_id: get(rate, 'rate_id'),
    ...getFedExPaymentParams(config, SHIPPO),
    ...getPackagesParam({
      config,
      shipperType: SHIPPO,
      otherParams: get(config, 'packages', []).map((parcel) =>
        omitBy(
          {
            sub_package_type: parcel[`${SHIPPO}__sub_package_type`],
            invoice: config.invoice_number,
          },
          isEmptyValue,
        ),
      ),
      rate,
    }),
  }
}

export function getUSPSShipParams(config, rate) {
  return {
    alternate_return_to_address: config.use_alternate_return_to_address
      ? config.alternate_return_to_address
      : null,
    ship_date: getShipDate(config.ship_date),
    nondelivery_option: config[`${USPS_SHIP}__nondelivery_option`],
    reason_for_export: config[`${USPS_SHIP}__reason_for_export`],
    reason_for_export_explanation: config.reason_for_export_explanation,
    mailing_zip_code: config.mailing_zip_code,
    processing_category: rate.processing_category,
    rate_indicator: rate.rate_indicator,
    invoice_number: config.invoice_number,
    hazmat_type: config[`${USPS_SHIP}__hazmat_type`],
    has_nonstandard_characteristics: config.has_nonstandard_characteristics,
    ignore_bad_address: config.ignore_bad_address,
    ...getTaxInfo(config, USPS_SHIP, SENDER_TAX_INFO),
    ...getTaxInfo(config, USPS_SHIP, RECEIVER_TAX_INFO),
    ...getPackagesParam({
      config,
      shipperType: USPS_SHIP,
      rate,
    }),
  }
}

export function getAustraliaPostParams(config, rate) {
  return {
    ship_date: getShipDate(config.ship_date),
    service_token: get(rate, 'service_token'),
    shipment_id: get(rate, 'shipment_id'),
    reason_for_export: config.australia_post__reason_for_export,
    reason_for_export_explanation: config.reason_for_export_explanation,
    ...getPackagesParam({
      config,
      shipperType: AUSTRALIA_POST,
      rate,
    }),
  }
}

export function getLabelRequestParams(
  config,
  shipper,
  labelType,
  rate,
  company,
  shipFromAddress,
  shipToAddress,
) {
  const shipperType = shipper.vendor

  const sharedParams = getSharedParams(
    config,
    shipper,
    labelType,
    shipFromAddress,
    shipToAddress,
    rate,
  )

  if (shipperType === UPS) {
    return {
      ...sharedParams,
      ...getUpsParams(config, rate),
    }
  }
  if (shipperType === FEDEX) {
    return {
      ...sharedParams,
      ...getFedExParams(config, rate, shipper),
    }
  }
  if (shipperType === FEDEX_360) {
    return {...sharedParams, ...getFedEx360Params(config, rate)}
  }
  if (shipperType === ENDICIA) {
    return {...sharedParams, ...getEndiciaParams(config, rate)}
  }
  if (shipperType === PITNEY) {
    return {...sharedParams, ...getPitneyParams(config, rate, company)}
  }
  if (shipperType === PITNEY_MERCHANT) {
    return {...sharedParams, ...getPitneyMerchantParams(config, rate, company)}
  }
  if (shipperType === PITNEY_PRESORT) {
    return {...sharedParams, ...getPitneyPresortParams(config, rate, company)}
  }
  if (shipperType === CANADA_POST) {
    return {...sharedParams, ...getCanadaPostParams(config, rate)}
  }
  if (shipperType === PITNEY_CBDS) {
    return {...sharedParams, ...getPitneyCBDSPostParams(config, rate)}
  }
  if (shipperType === DHL) {
    return {...sharedParams, ...getDHLParams(config, rate)}
  }
  if (shipperType === DHL_ECOMMERCE) {
    return {...sharedParams, ...getDHLEcommerceParams(config, rate, labelType)}
  }
  if (shipperType === AMAZON_SFP) {
    return {...sharedParams, ...getAmazonParams(config, rate)}
  }
  if (shipperType === AMAZON_SHIPPER) {
    return {...sharedParams, ...getAmazonShipperParams(config, rate)}
  }
  if (shipperType === AUSTRALIA_POST) {
    return {...sharedParams, ...getAustraliaPostParams(config, rate)}
  }
  if (shipperType === NEWGISTICS) {
    return {...sharedParams, ...getNewgisticsParams(config, rate)}
  }
  if (shipperType === SENDLE) {
    return {...sharedParams, ...getSendleParams(config, rate)}
  }
  if (shipperType === SHIPPO) {
    return {...sharedParams, ...getShippoParams(config, rate)}
  }
  if (shipperType === USPS_SHIP) {
    return {...sharedParams, ...getUSPSShipParams(config, rate, company)}
  }

  throw new Error(`Unexpected shipper: ${shipperType}`)
}

export const labelRequestSelector = createSelector(
  labelConfigSelector,
  labelShipperSelector,
  labelTypeSelector,
  selectedRateSelector,
  companySelector,
  labelShipFromAddressSelector,
  labelShipToAddressSelector,
  (config, shipper, labelType, rate, company, shipFromAddress, shipToAddress) =>
    omitBy(
      getLabelRequestParams(
        config,
        shipper,
        labelType,
        rate,
        company,
        shipFromAddress,
        shipToAddress,
      ),
      isEmptyValue,
    ),
)

export function labelRequestURLSelector(state, {labelInfoID}) {
  const shipperType = labelShipperTypeSelector(state, {labelInfoID})
  const orderType = orderTypeSelector(state, {labelInfoID})

  let baseURL
  if (orderType === OT_V3_ORDER) {
    const orderNumber = orderNumberFromLabelConfigSelector(state, {
      labelInfoID,
    })
    baseURL = orderLinkSelector(state, {orderNumber})
  }
  if (orderType === OT_RETURN_ORDER) {
    baseURL = '/return_order'
  }

  return `${baseURL}/label/${encodeURIComponent(
    shipperType === PITNEY_MERCHANT ? PITNEY : shipperType,
  )}`
}

export async function createLabelImage(labelInfoID) {
  try {
    const labelRequestParams = labelRequestSelector(getState(), {labelInfoID})
    const orderType = orderTypeSelector(getState(), {labelInfoID})

    if (orderType === OT_V3_ORDER) {
      delete labelRequestParams.ship_to

      assertAmazonPrimeSanity(labelInfoID, labelRequestParams)
    }

    const url = labelRequestURLSelector(getState(), {labelInfoID})

    await apiverson.post(url, labelRequestParams)

    if (orderType === OT_V3_ORDER) {
      const orderNumber = orderNumberFromLabelConfigSelector(getState(), {
        labelInfoID,
      })
      await updateOrders([orderNumber])
    }
    if (orderType === OT_RETURN_ORDER) {
      const referenceID = referenceIDFromLabelConfigSelector(getState(), {
        labelInfoID,
      })
      await updateReturnOrders([referenceID])
    }
  } catch (err) {
    const message = `Error creating label image: ${labelInfoID}. ${err.message}`
    updateLabelInfo(labelInfoID, {error_message: message})

    await saveLabelInfo(labelInfoID)
  } finally {
    updateLabelInfo(labelInfoID, {label_loading: false})
  }
}

export async function createLabelImages(
  labelInfoIDs,
  {displayProcessLabelModal = true} = {},
) {
  const currentPage = currentPageSelector(getState())

  displayProcessLabelModal =
    displayProcessLabelModal &&
    [ORDER_LIST_PAGE, ORDER_DETAIL_PAGE].includes(currentPage)

  try {
    if (displayProcessLabelModal) {
      showProcessLabelModal({labelInfoIDs})
    }

    addPendingLabels(labelInfoIDs)

    for (const labelInfoID of labelInfoIDs) {
      updateLabelInfo(labelInfoID, {label_loading: true})
    }

    const batchSize = 10
    const batches = chunk(labelInfoIDs, batchSize)
    for (const ids of batches) {
      await Promise.all(ids.map((labelInfoID) => createLabelImage(labelInfoID)))
    }

    if (currentPage === ORDER_LIST_PAGE) {
      await refreshOrderList()
    } else if (currentPage === ORDER_DETAIL_PAGE) {
      await loadOrderDetailOrder({reload: true})
    } else if (currentPage === PACK_SHIP_PAGE) {
      const orderNumbers = orderNumbersFromLabelConfigsSelector(getState(), {
        labelInfoIDs,
      })
      await updateOrders(orderNumbers)
    }
  } catch (err) {
    logError(err, 'Error creating label images')
  } finally {
    await updateOrderCounts()

    removePendingLabels(labelInfoIDs)
  }
}
